/**
 * Map Service
 * Handles map-related operations and utilities
 */
export default class MapService {
  constructor() {
    this.mapboxToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'
  }

  /**
   * Initialize Mapbox map
   * @param {string} containerId - The container element ID
   * @param {Array} center - Center coordinates [lng, lat]
   * @param {number} zoom - Initial zoom level
   * @returns {Object} Mapbox map instance
   */
  initializeMap(containerId, center = [-79.5, 42.4], zoom = 15) {
    // Set Mapbox access token
    // eslint-disable-next-line no-undef
    if (typeof mapboxgl !== 'undefined') {
      // eslint-disable-next-line no-undef
      mapboxgl.accessToken = this.mapboxToken
      
      // Initialize Mapbox map
      // eslint-disable-next-line no-undef
      return new mapboxgl.Map({
        container: containerId,
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: center,
        zoom: zoom
      })
    } else {
      throw new Error('Mapbox GL JS not loaded')
    }
  }

  /**
   * Add blocks layer to map
   * @param {Object} map - Mapbox map instance
   * @param {Object} blocksGeoJson - Blocks GeoJSON data
   * @param {Object} vizSettings - Visualization settings
   */
  addBlocksLayer(map, blocksGeoJson, vizSettings = {}) {
    if (!blocksGeoJson || !blocksGeoJson.features.length) {
      console.log('No blocks GeoJSON available')
      return
    }

    // Add blocks source
    map.addSource('blocks-source', {
      type: 'geojson',
      data: blocksGeoJson
    })

    // Add blocks fill layer
    map.addLayer({
      id: 'blocks-fill',
      type: 'fill',
      source: 'blocks-source',
      paint: {
        'fill-color': vizSettings.blockColor || '#6e59c7',
        'fill-opacity': (vizSettings.colorOpacity ?? 0.8)
      }
    })

    // Add blocks outline layer
    map.addLayer({
      id: 'blocks-outline',
      type: 'line',
      source: 'blocks-source',
      paint: {
        'line-width': vizSettings.lineWidth || 2,
        'line-color': vizSettings.lineColor || '#eee'
      }
    })

    // Add block labels
    map.addLayer({
      id: 'blocks-labels',
      type: 'symbol',
      source: 'blocks-source',
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2
      }
    })
  }

  /**
   * Fit map to blocks bounds
   * @param {Object} map - Mapbox map instance
   * @param {Object} blocksGeoJson - Blocks GeoJSON data
   */
  fitMapToBlocks(map, blocksGeoJson) {
    if (!blocksGeoJson || !blocksGeoJson.features.length) {
      return
    }

    try {
      // Calculate bounds
      // eslint-disable-next-line no-undef
      const bounds = new mapboxgl.LngLatBounds()
      
      blocksGeoJson.features.forEach(feature => {
        const coordinates = feature.geometry.coordinates
        if (feature.geometry.type === 'Polygon') {
          coordinates[0].forEach(coord => {
            if (!isNaN(coord[0]) && !isNaN(coord[1])) {
              bounds.extend(coord)
            }
          })
        }
      })

      map.fitBounds(bounds, {
        padding: 50,
        animate: false
      })
    } catch (error) {
      console.error('Error fitting map to blocks:', error)
    }
  }

  /**
   * Clear satellite layers from map
   * @param {Object} map - Mapbox map instance
   */
  async clearSatelliteLayers(map) {
    const layersToRemove = ['satellite-preview', 'block-highlight', 'scene-info', 'background-warning']
    const sourcesToRemove = ['satellite-preview', 'block-highlight', 'scene-info', 'background-warning']

    layersToRemove.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
      }
    })

    sourcesToRemove.forEach(sourceId => {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    })

    // Wait for map to be idle to ensure all removals are processed
    await new Promise(resolve => {
      if (map.loaded()) {
        map.once('idle', () => {
          console.log('Map idle after clearing layers/sources.')
          resolve()
        })
      } else {
        resolve()
      }
    })
  }

  /**
   * Add satellite imagery layer to map
   * @param {Object} map - Mapbox map instance
   * @param {string} imageUrl - Image URL
   * @param {Array} coordinates - Image coordinates
   */
  addSatelliteLayer(map, imageUrl, coordinates) {
    try {
      // Add the satellite image layer
      map.addSource('satellite-preview', {
        type: 'image',
        url: imageUrl,
        coordinates: coordinates
      })
      
      map.addLayer({
        id: 'satellite-preview',
        type: 'raster',
        source: 'satellite-preview',
        paint: { 
          'raster-opacity': 0.9,
          'raster-resampling': 'linear'
        }
      }, 'blocks-outline')
      
      console.log('✅ Satellite imagery layer added successfully')
    } catch (error) {
      console.error('Error adding satellite imagery layer:', error)
      throw error
    }
  }

  /**
   * Add block highlight to map
   * @param {Object} map - Mapbox map instance
   * @param {Object} blockFeature - Block feature to highlight
   */
  addBlockHighlight(map, blockFeature) {
    // Add the block highlight
    map.addSource('block-highlight', {
      type: 'geojson',
      data: blockFeature
    })
    
    map.addLayer({
      id: 'block-highlight',
      type: 'line',
      source: 'block-highlight',
      paint: { 
        'line-color': '#ff0000',
        'line-width': 3,
        'line-opacity': 0.8
      }
    })
  }

  /**
   * Add scene info overlay to map
   * @param {Object} map - Mapbox map instance
   * @param {Object} sceneInfo - Scene information
   */
  addSceneInfoOverlay(map, sceneInfo) {
    map.addSource('scene-info', {
      type: 'geojson',
      data: sceneInfo
    })
    
    map.addLayer({
      id: 'scene-info',
      type: 'symbol',
      source: 'scene-info',
      layout: {
        'text-field': ['concat', '📡 ', ['get', 'title'], '\n', ['get', 'datetime'], '\nCloud Cover: ', ['get', 'cloudCover']],
        'text-size': 12,
        'text-anchor': 'top',
        'text-offset': [0, 1],
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2
      }
    })
  }

  /**
   * Get map center from farm location
   * @param {Object} farm - Farm object with geolocation
   * @returns {Array} Center coordinates [lng, lat]
   */
  getMapCenter(farm) {
    // Use farm location if available, otherwise default
    if (farm && farm.geolocation) {
      return [
        farm.geolocation.longitude,
        farm.geolocation.latitude
      ]
    }
    return [-79.5, 42.4] // Default center
  }
}
